import { Component, h } from 'preact'

import Header from '../header'
import Panel from '../panel'
import ButtonControl from '../button-control'
import DropdownControl from '../dropdown/DropdownControl'
import InputGroup from '../ui-components/InputGroup'
import ColorSelector from '../community-header/ColorSelector'
import { FooterPanel, Link }  from './footerPanel'
import AlertComponent from '../ui-components/AlertComponent'
import Loader from '../ui-components/Loader'
import { 
  getEmbeddableWidgetConfig,
  EmbeddableWidgetConfig, 
  updateEmbeddableWidgetConfig,
  getEmbeddableWidgetBundle
} from '../../utils/api'

const TRIGGER_TYPES = ['button', 'floating']
const TRIGGER_POSITIONS = ['left', 'right']
const USER_AVATAR_SHAPES = ['circle', 'square', 'rounded-square']

interface Props {}

interface FooterConfiguration {
  title?: string
  links?: Array <Link>
}

interface State {
  success: boolean,
  error: boolean,
  isLoading: boolean,
  triggerType: string,
  triggerText: string,
  triggerColor: string,
  triggerBgColor: string,
  triggerPosition: string,
  headerTitle: string,
  headerTextColor: string,
  headerBgColor: string,
  headerShowShadow: boolean,
  cardUserAvatarShape: 'circle'| 'square' | 'rounded-square',
  cardTitleColor: string,
  cardMetadetaColor: string,
  cardBorderColor: string,
  footerConfiguration: FooterConfiguration
}

class EmbeddableWidgetConfigurator extends Component<Props, State> {
  script: HTMLScriptElement | null

  constructor () {
    super()
    this.script = null

    this.state = {
      success: false,
      error: false,
      isLoading: false,
      triggerText: 'Community Help',
      triggerColor: '#ffffff',
      triggerBgColor: '#1dadf5',
      triggerType: 'button',
      triggerPosition: 'right',
      headerTitle: '#Community',
      headerTextColor: '#DE8E8E',
      headerBgColor: '#1DADF5',
      headerShowShadow: true,
      cardUserAvatarShape: 'circle',
      cardTitleColor: '#1A1A1A',
      cardMetadetaColor: 'A7AEB5',
      cardBorderColor: '#f2f2f2',
      footerConfiguration: {
        title: '',
        links: []
      }
    }
  }

  async componentDidMount() {
    this.setStateFromBackgend(await getEmbeddableWidgetConfig())

    window.addEventListener('insided:conversational:loaded', ()=> {
      this.setState({ success: true, isLoading: false })
    })
  }

  setStateFromBackgend(data: EmbeddableWidgetConfig) {
    this.setState({
      triggerText: data.trigger_text,
      triggerColor: data.trigger_color,
      triggerBgColor: data.brand_color,
      triggerType: data.trigger_type,
      triggerPosition: data.position,
      headerTitle: data.header_title,
      headerTextColor: data.header_color,
      headerBgColor: data.header_background,
      headerShowShadow: data.header_shadow,
      cardUserAvatarShape: data.card_user_avatar_radius,
      cardTitleColor: data.card_title_color,
      cardMetadetaColor: data.card_metadata_color,
      cardBorderColor: data.card_border_color,
      footerConfiguration: JSON.parse(data.footer_configuration as string)
    })
  }

  async preview () {

    if (window.inSided && window.inSided.conversational) {
      window.inSided.conversational.destroy()
    }

    this.setState({ success: false, isLoading: true })

    const bundleSrc = await getEmbeddableWidgetBundle({
      trigger_text: this.state.triggerText,
      trigger_color: this.state.triggerColor,
      brand_color: this.state.triggerBgColor,
      trigger_type: this.state.triggerType as ('button' | 'floating'),
      position: this.state.triggerPosition as 'left' | 'right',
      header_title: this.state.headerTitle,
      header_color: this.state.headerTextColor,
      header_background: this.state.headerBgColor,
      header_shadow: this.state.headerShowShadow,
      card_user_avatar_radius: this.state.cardUserAvatarShape,
      card_title_color: this.state.cardTitleColor,
      card_metadata_color: this.state.cardMetadetaColor,
      card_border_color: this.state.cardBorderColor,
      footer_configuration: this.state.footerConfiguration,
      powered_by_enabled: true
    })

    if (this.script) {
      this.script.parentNode?.removeChild(this.script)
    }

    this.script = document.createElement("script");

    this.script.src = bundleSrc;
    this.script.async = true;

    document.body.appendChild(this.script);
  }

  async save () {
    this.setState({ error: false, success: false, isLoading: true })

    try {
      await updateEmbeddableWidgetConfig({
        trigger_text: this.state.triggerText,
        trigger_color: this.state.triggerColor,
        brand_color: this.state.triggerBgColor,
        trigger_type: this.state.triggerType as ('button' | 'floating'),
        position: this.state.triggerPosition as 'left' | 'right',
        header_title: this.state.headerTitle,
        header_color: this.state.headerTextColor,
        header_background: this.state.headerBgColor,
        header_shadow: this.state.headerShowShadow,
        card_user_avatar_radius: this.state.cardUserAvatarShape,
        card_title_color: this.state.cardTitleColor,
        card_metadata_color: this.state.cardMetadetaColor,
        card_border_color: this.state.cardBorderColor,
        footer_configuration: this.state.footerConfiguration,
        powered_by_enabled: true
      })
    } catch (err) {
      this.setState({ error: true, isLoading: false })
      return
    }
    
    this.setState({ isLoading: false, success: true })
  }
  
  normalizeTriggerTypes () {
    return TRIGGER_TYPES.map((type: string) => {
      return {
        title: type,
        value: type,
        onClick: () => this.setState({ triggerType: type })
      }
    })
  }

  normalizeTriggerPositions () {
    return TRIGGER_POSITIONS.map((position: string) => {
      return {
        title: position,
        value: position,
        onClick: () => this.setState({ triggerPosition: position })
      }
    })
  }

  normalizeAvatarShapes () {
    return USER_AVATAR_SHAPES.map((shape: string) => {
      return {
        title: shape,
        value: shape,
        onClick: () => this.setState({ cardUserAvatarShape: shape as 'circle' | 'square' | 'rounded-square' })
      }
    })
  }

  render (
    props: Props, 
    { 
      isLoading, 
      triggerType, 
      triggerPosition, 
      triggerText, 
      triggerColor, 
      triggerBgColor,
      headerTitle,
      headerTextColor,
      headerBgColor,
      headerShowShadow,
      cardUserAvatarShape,
      cardTitleColor,
      cardMetadetaColor,
      cardBorderColor,
      footerConfiguration,
      success
    } : State
  ) {

    return (
      <div className='conversational-widget-new'>
        <Header
          title='Customization'
          saveCallback={() => this.save()}
          customButtons={[{
            variant: 'primary',
            title: 'Preview',
            action: () => this.preview(),
            customClass: 'conversational_preview-button'
          }]} 
        />
        { success && <AlertComponent
          messageType='success'
          isVisibleFor={3000}
        /> }
        { isLoading && <Loader /> }
        <Panel title='Trigger'>
          <label className='trigger-label'>Applicable for embeddable widget only.</label>
          <div className='trigger_wrapper-dropdowns form-group'>
            <div>
              <label>Trigger type</label>
              <DropdownControl 
                align='adjust'
                triggerClass='trigger_dropdown'
                // @ts-ignore
                items={this.normalizeTriggerTypes()} 
                trigger={<ButtonControl icon='arrowDropdown' title={triggerType} class='half-dropdowns trigger-type' />}/>
            </div>
            <div>
              <label>Trigger position</label>
              <DropdownControl
                align='adjust'
                triggerClass='trigger_dropdown'
                // @ts-ignore
                items={this.normalizeTriggerPositions()}
                trigger={<ButtonControl icon='arrowDropdown' title={triggerPosition} class='half-dropdowns trigger-position'/>}
              />
            </div>
          </div>
          <InputGroup
              title='Trigger text'
              type='text'
              onChange={(e: { target: HTMLInputElement }) => {
                this.setState({ triggerText: e.target.value })
              }}              
              value={triggerText} />
          <div className='wrapper-trigger_colors'>
            <ColorSelector
              title='Text Color'
              className='text_color'
              selectedColor={triggerColor}
              onCallback={(color: string) => this.setState({ triggerColor: color })}
            />
            <ColorSelector
              title='Background Color'
              className='background_color'
              selectedColor={triggerBgColor}
              onCallback={(color: string) => this.setState({ triggerBgColor: color })}
            />
          </div>
        </Panel>
        <Panel title='Header'>
          <label className='trigger-label'>Applicable for embeddable widget only.</label>
          <InputGroup
            title='Title on home page'
            type='text'
            onChange={(e: { target: HTMLInputElement }) => {
              this.setState({ headerTitle: e.target.value })
            }}              
            value={headerTitle} />
          <div className='wrapper-trigger_colors'>
            <ColorSelector
              title='Text Color'
              className='text_color'
              selectedColor={headerTextColor}
              onCallback={(color: string) => this.setState({ headerTextColor: color })}
            />
            <ColorSelector
              title='Background Color'
              className='background_color'
              selectedColor={headerBgColor}
              onCallback={(color: string) => this.setState({ headerBgColor: color })}
            />
          </div>
          <div className='header_shadow_checkbox'>
            <InputGroup
              title='Show a shadow below widget header'
              type='checkbox'
              checked={headerShowShadow}
              onChangeCheckbox={() => { this.setState({ headerShowShadow: !this.state.headerShowShadow }) }}
              value='preview'
            />
          </div>
        </Panel>
        <Panel title='Topic card'>
          <label className='trigger-label'>Applicable for both embeddable widget and in-page widget.</label>
          <div className='wrapper-trigger_colors'>
            <ColorSelector
              title='Title Text Color'
              className='text_color'
              selectedColor={cardTitleColor}
              onCallback={(color: string) => this.setState({ cardTitleColor: color })}
            />
            <ColorSelector
              title='Metadata Text Color'
              className='background_color'
              selectedColor={cardMetadetaColor}
              onCallback={(color: string) => this.setState({ cardMetadetaColor: color })}
            />
          </div>
          <div className='wrapper-trigger_colors'>
            <ColorSelector
              title='Border Color'
              className='text_color'
              selectedColor={cardBorderColor}
              onCallback={(color: string) => this.setState({ cardBorderColor: color })}
            />
            <div className='topic-card_user-avatar-shapes'>
              <label>User Avatar Shape</label>
              <DropdownControl 
                align='adjust'
                // @ts-ignore
                items={this.normalizeAvatarShapes()} 
                trigger={<ButtonControl icon='arrowDropdown' title={cardUserAvatarShape} class='half-dropdowns' />}/>
            </div>
          </div>
        </Panel>
        <FooterPanel 
          footerTitle = {footerConfiguration.title || ''}
          links={footerConfiguration.links || []}
          onTitleChanged={(title: string) => {
            let currentFooterConfiguration = this.state.footerConfiguration
            currentFooterConfiguration.title = title
            this.setState({ footerConfiguration: currentFooterConfiguration })
          }}
          onAddLink={(title: string, url: string) => {
            if (title && url) {
              let currentFooterConfiguration = this.state.footerConfiguration
              let newLinks = [...(currentFooterConfiguration.links || []), {
                title: title,
                url: url
              }]
              currentFooterConfiguration.links = newLinks
              this.setState({ footerConfiguration: currentFooterConfiguration })
            }
          }}
          onRemoveLink={(idx: number) => {
            let currentFooterConfiguration = this.state.footerConfiguration
            let links = currentFooterConfiguration.links || []
            links.splice(idx, 1)
            currentFooterConfiguration.links = links
            this.setState({ footerConfiguration: currentFooterConfiguration })
          }}
          onLinkChanged={(idx: number, newTitle: string, newUrl: string) => {
            let newLinks = [...this.state.footerConfiguration.links || []]
            let currentFooterConfiguration = this.state.footerConfiguration
            newLinks[idx].title = newTitle
            newLinks[idx].url = newUrl
            currentFooterConfiguration.links = newLinks
            this.setState({ footerConfiguration: currentFooterConfiguration })
          }}
        />
      </div>
    )
  }
}

export default EmbeddableWidgetConfigurator
